import React from 'react';
import { TextField, InputAdornment, IconButton } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';

/**
 * QuickFilter - Search input for AG Grid quick filtering
 *
 * @param {Object} props
 * @param {string} props.value - Current filter value
 * @param {Function} props.onChange - Called when filter value changes
 * @param {string} [props.placeholder='Search...'] - Placeholder text
 * @param {Object} [props.sx] - Additional MUI sx styles
 * @param {string} [props.size='small'] - TextField size
 * @param {boolean} [props.showClearButton=true] - Show clear button when value exists
 * @param {boolean} [props.autoFocus=false] - Auto focus on mount
 *
 * @example
 * const [filterText, setFilterText] = useState('');
 *
 * <QuickFilter
 *   value={filterText}
 *   onChange={setFilterText}
 *   placeholder="Filter entries..."
 * />
 *
 * <AgGridReact quickFilterText={filterText} />
 */
export const QuickFilter = ({
  value,
  onChange,
  placeholder = 'Search...',
  sx = {},
  size = 'small',
  showClearButton = true,
  autoFocus = false,
  ...textFieldProps
}) => {
  const handleChange = (event) => {
    onChange(event.target.value);
  };

  const handleClear = () => {
    onChange('');
  };

  return (
    <TextField
      value={value}
      onChange={handleChange}
      placeholder={placeholder}
      size={size}
      autoFocus={autoFocus}
      sx={{
        minWidth: 200,
        '& .MuiOutlinedInput-root': {
          backgroundColor: 'background.paper',
        },
        ...sx,
      }}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <SearchIcon color="action" fontSize="small" />
          </InputAdornment>
        ),
        endAdornment: showClearButton && value ? (
          <InputAdornment position="end">
            <IconButton
              size="small"
              onClick={handleClear}
              edge="end"
              aria-label="clear filter"
            >
              <ClearIcon fontSize="small" />
            </IconButton>
          </InputAdornment>
        ) : null,
      }}
      {...textFieldProps}
    />
  );
};

export default QuickFilter;
